import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Download, FileText, Search } from "lucide-react";
import useDocumentTitle from "../lib/useDocumentTitle";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function formatSize(bytes) {
  if (!bytes) return "";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export default function Guides() {
  useDocumentTitle("Free PDF Guides", "Free downloadable PDF guides on home design, Vastu and construction planning from HouseDesignWala™ — no signup required.");
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");

  useEffect(() => {
    api.get("/guides").then((r) => setGuides(r.data)).finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const set = new Set(guides.map((g) => g.category).filter(Boolean));
    return ["all", ...Array.from(set)];
  }, [guides]);

  const filtered = guides.filter((g) => {
    const matchesCategory = category === "all" || g.category === category;
    const q = search.toLowerCase();
    const matchesSearch = !q || g.title.toLowerCase().includes(q) || (g.description || "").toLowerCase().includes(q);
    return matchesCategory && matchesSearch;
  });

  return (
    <div>
      <section className="bg-[#FEF3E2] py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#F7941D]">Free Resources</span>
          <h1 className="font-display text-4xl lg:text-5xl font-extrabold text-[#1a3c5e] mt-2">Free PDF Design Guides</h1>
          <p className="mt-3 text-gray-700 max-w-2xl mx-auto">
            Practical, no-cost guides on home design, Vastu, construction planning and more — download instantly, no strings attached.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between mb-8">
          <div className="relative w-full sm:max-w-xs">
            <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search guides..."
              data-testid="guides-search"
              className="w-full border border-[#1a3c5e]/15 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#F7941D] focus:border-transparent"
            />
          </div>
          {categories.length > 1 && (
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  data-testid={`guides-filter-${c.toLowerCase()}`}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide transition-colors ${
                    category === c ? "bg-[#1a3c5e] text-white" : "bg-white text-[#1a3c5e] border border-[#1a3c5e]/15 hover:bg-[#FEF3E2]"
                  }`}
                >
                  {c === "all" ? "All" : c}
                </button>
              ))}
            </div>
          )}
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="rounded-2xl bg-white border border-[#1a3c5e]/10 overflow-hidden animate-pulse">
                <div className="aspect-[16/10] bg-gray-100" />
                <div className="p-5 space-y-3">
                  <div className="h-3 w-16 bg-gray-100 rounded" />
                  <div className="h-4 w-3/4 bg-gray-100 rounded" />
                  <div className="h-3 w-full bg-gray-100 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <FileText className="w-10 h-10 text-[#1a3c5e]/20 mx-auto mb-3" />
            <p className="text-gray-500">No guides found{search ? ` for "${search}"` : ""}.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((g) => (
              <GuideCard key={g.id} guide={g} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function GuideCard({ guide }) {
  const downloadUrl = `${BACKEND_URL}/api/guides/${guide.slug}/download`;
  return (
    <article
      data-testid={`guide-card-${guide.slug}`}
      className="bg-white rounded-2xl overflow-hidden border border-[#1a3c5e]/10 shadow-sm flex flex-col h-full transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg"
    >
      <div className="aspect-[16/10] overflow-hidden relative bg-gradient-to-br from-[#1a3c5e] to-[#0f2438]">
        {guide.cover_image ? (
          <img src={guide.cover_image} alt={guide.title} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <FileText className="w-14 h-14 text-white/25" />
          </div>
        )}
        <span className="absolute top-3 right-3 flex items-center gap-1 bg-white/95 backdrop-blur text-[#1a3c5e] text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-sm">
          <FileText className="w-3 h-3" /> PDF{guide.file_size ? ` · ${formatSize(guide.file_size)}` : ""}
        </span>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-[#FEF3E2] text-[#F7941D] w-fit">
          {guide.category}
        </span>
        <h3 className="font-display font-bold text-lg text-[#1a3c5e] mt-2 leading-tight line-clamp-2">{guide.title}</h3>
        {guide.description && (
          <p className="text-sm text-gray-600 mt-2 line-clamp-3 flex-1">{guide.description}</p>
        )}
        <a
          href={downloadUrl}
          data-testid={`guide-download-${guide.slug}`}
          className="mt-4 inline-flex items-center justify-center gap-2 bg-[#F7941D] hover:bg-[#e0861a] text-white rounded-xl font-semibold px-4 py-2.5 text-sm shadow-md hover:shadow-lg transition-all duration-200"
        >
          <Download className="w-4 h-4" /> Download Free PDF
        </a>
      </div>
    </article>
  );
}
